import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/* AXIOS */
const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem(`${role}Token`);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* DATE HELPERS */
const getNowDateTimeLocal = () => {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

export default function VipAutoAssign() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const vip = state?.vip;

  const [vipStatus, setVipStatus] = useState("inactive");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showTimeModal, setShowTimeModal] = useState(false);
  const [startAt, setStartAt] = useState(getNowDateTimeLocal());
  const [endAt, setEndAt] = useState(getNowDateTimeLocal());

  const [securityGroups, setSecurityGroups] = useState([]);
  const [rankOptions, setRankOptions] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [gradeList, setGradeList] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");

  useEffect(() => {
    if (!vip) {
      navigate("/vip-management");
      return;
    }
    fetchAssignedGuards();
  }, [vip]);

  const fetchAssignedGuards = async () => {
    try {
      const res = await api.get(`/api/assignments/${vip.id}`);
      const assignment = res.data?.data || res.data;

      if (assignment && Array.isArray(assignment.officer) && assignment.officer.length > 0) {
        setResult({
          officers: assignment.officer,
          status: assignment.status,
          assignedAt: assignment.assignedAt,
          startAt: assignment.startAt,
          endAt: assignment.endAt,
        });
        setVipStatus("active");
      } else {
        setVipStatus("inactive");
        setResult(null);
      }
    } catch (err) {
      setVipStatus("inactive");
      setResult(null);
    }
  };

  const fetchSecurityGroups = async () => {
    try {
      const role = localStorage.getItem("role");
      const token = localStorage.getItem(`${role}Token`);
      const res = await axios.get(`${BASE_URL}/api/setting/getsecuritygroup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSecurityGroups(res.data || []);
    } catch {
      toast.error("Failed to load security groups");
    }
  };

  const fetchUniqueRanks = async () => {
    try {
      const role = localStorage.getItem("role");
      const token = localStorage.getItem(`${role}Token`);
      const res = await axios.get(`${BASE_URL}/api/officer/unique-ranks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRankOptions(res.data || []);
    } catch {
      toast.error("Failed to load ranks");
    }
  };

  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setSelectedGrade("");
    setGradeList([]);

    const group = securityGroups.find((g) => g.id === Number(groupId));
    if (!group) return;

    setGradeList(
      group.values.map((v) => ({
        rank: v.rank,
        total: v.value,
      }))
    );
  };

  const handleAddGrade = () => {
    if (!selectedGrade) return toast.error("Select rank first");
    if (gradeList.find((g) => g.rank === selectedGrade))
      return toast.error("Rank already added");

    setGradeList([...gradeList, { rank: selectedGrade, total: "" }]);
    setSelectedGrade("");
  };

  const handleChange = (index, value) => {
    const updated = [...gradeList];
    updated[index].total = value;
    setGradeList(updated);
  };

  const handleRemove = (index) => {
    const updated = [...gradeList];
    updated.splice(index, 1);
    setGradeList(updated);
  };

  const handleAutoAssign = async () => {
    if (!startAt || !endAt) return toast.error("Select start & end time");
    if (gradeList.length === 0) return toast.error("Add at least one rank");

    for (let g of gradeList) {
      if (!g.total || g.total <= 0) {
        toast.error(`Enter valid number for ${g.rank}`);
        return;
      }
    }

    const payload = {
      userId: vip.id,
      startAt,
      endAt,
      levels: gradeList.map((g) => ({
        guardLevel: g.rank,
        numberOfGuards: Number(g.total),
      })),
    };

    try {
      setLoading(true);
      const res = await api.post(`/api/assignments/auto`, payload);
      toast.success(res.data?.message || "Officers Assigned Successfully");
      setShowTimeModal(false);
      fetchAssignedGuards();
    } catch (err) {
      toast.error(err.response?.data?.message || "Assignment Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!window.confirm("Mark duty as COMPLETED?")) return;
    try {
      setLoading(true);
      await api.post(`/api/assignments/complete/vip/${vip.id}/Completed`);
      toast.success("Duty Completed");
      setVipStatus("inactive");
      setResult(null);
    } catch {
      toast.error("Failed to complete duty");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCanceled = async () => {
    if (!window.confirm("Mark duty as CANCELED?")) return;
    try {
      setLoading(true);
      await api.post(`/api/assignments/complete/vip/${vip.id}/Canceled`);
      toast.success("Duty Canceled");
      setVipStatus("inactive");
      setResult(null);
    } catch {
      toast.error("Failed to cancel duty");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="assign-wrapper">
      {/* Header */}
      <header className="assign-header">
        <h1>VIP Officer Assignment</h1>
        <p>Auto-assign security officers based on VIP requirements</p>
      </header>

      {/* VIP Info Card */}
      <section className="vip-card">
        <div className="vip-info">
          <h2>{vip?.name}</h2>
          <p><i className="fas fa-envelope"></i> {vip?.email}</p>
          <p><i className="fas fa-briefcase"></i> {vip?.designation}</p>
          <p><i className="fas fa-phone"></i> {vip?.contactno}</p>
        </div>
        <div className="status-badge">
          <span className={vipStatus === "active" ? "active" : "inactive"}>
            {vipStatus === "active" ? "Active Duty" : "Inactive"}
          </span>
        </div>
      </section>

      {/* Action Buttons */}
      <section className="action-buttons">
        {vipStatus === "active" && (
          <>
            <button className="btn complete" onClick={handleMarkCompleted}>
              <i className="fas fa-check-circle"></i> Mark as Completed
            </button>
            <button className="btn cancel" onClick={handleMarkCanceled}>
              <i className="fas fa-times-circle"></i> Mark as Canceled
            </button>
          </>
        )}

        {vipStatus === "inactive" && (
          <button
            className="btn assign"
            onClick={() => {
              fetchSecurityGroups();
              fetchUniqueRanks();
              setShowTimeModal(true);
            }}
          >
            <i className="fas fa-users-cog"></i> Auto Assign Officers
          </button>
        )}
      </section>

      {/* Assigned Officers Table */}
      {result && (
        <section className="officers-section">
          <div className="table-card">
            <h3>Assigned Officers ({result.officers.length})</h3>
            <div className="table-container">
              <table className="officers-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Rank</th>
                    <th>Experience</th>
                    <th>Status</th>
                    <th>Assigned At</th>
                    <th>Start At</th>
                    <th>End At</th>
                  </tr>
                </thead>
                <tbody>
                  {result.officers.map((officer) => (
                    <tr key={officer.id}>
                      <td className="name">{officer.name}</td>
                      <td>{officer.email}</td>
                      <td>
                        <span className="rank-badge">{officer.rank}</span>
                      </td>
                      <td>{officer.experience} yrs</td>
                      <td>
                        <span className={`status-badge small ${result.status.toLowerCase()}`}>
                          {result.status}
                        </span>
                      </td>
                      <td>
                        {result.assignedAt
                          ? new Date(result.assignedAt.split(".")[0]).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "—"}
                      </td>
                      <td>
                        {result.startAt
                          ? new Date(result.startAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "—"}
                      </td>
                      <td>
                        {result.endAt
                          ? new Date(result.endAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Auto Assign Modal */}
      {showTimeModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Auto Assign Officers</h3>
              <button onClick={() => setShowTimeModal(false)} className="close-btn">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={startAt}
                  min={getNowDateTimeLocal()}
                  onChange={(e) => setStartAt(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={endAt}
                  min={startAt || getNowDateTimeLocal()}
                  onChange={(e) => setEndAt(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Security Group</label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => handleSelectGroup(e.target.value)}
                >
                  <option value="">Select Group</option>
                  {securityGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedGroupId && (
                <>
                  <div className="form-group">
                    <label>Add Custom Rank</label>
                    <div className="add-rank">
                      <select
                        value={selectedGrade}
                        onChange={(e) => setSelectedGrade(e.target.value)}
                      >
                        <option value="">Select Rank</option>
                        {rankOptions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button onClick={handleAddGrade} className="add-btn">
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  <div className="grade-list">
                    {gradeList.map((g, i) => (
                      <div key={i} className="grade-item">
                        <span className="rank">{g.rank}</span>
                        <input
                          type="number"
                          min="1"
                          placeholder="Count"
                          value={g.total}
                          onChange={(e) => handleChange(i, e.target.value)}
                        />
                        <button onClick={() => handleRemove(i)} className="remove-btn">
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button onClick={handleAutoAssign} className="btn confirm" disabled={loading}>
                  {loading ? "Assigning..." : "Confirm Assignment"}
                </button>
                <button onClick={() => setShowTimeModal(false)} className="btn cancel">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium Dark Glassmorphic Styling - Matching UserDashboard */}
      <style jsx>{`
        .assign-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 2rem;
        }

        .assign-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .assign-header h1 {
          font-size: 2.3rem;
          font-weight: 700;
          color: #34d399;
          margin-bottom: 0.5rem;
        }

        .assign-header p {
          opacity: 0.85;
          font-size: 1.1rem;
          color: #cbd5e1;
        }

        .vip-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(16, 185, 129, 0.3);
          margin-bottom: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .vip-info h2 {
          font-size: 1.8rem;
          margin-bottom: 1rem;
          color: #ffffff;
        }

        .vip-info p {
          margin: 0.6rem 0;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          font-size: 1rem;
          color: #cbd5e1;
        }

        .vip-info i {
          color: #34d399;
          width: 20px;
        }

        .status-badge {
          font-size: 1.1rem;
          font-weight: 600;
        }

        .status-badge span {
          padding: 0.7rem 1.4rem;
          border-radius: 30px;
        }

        .status-badge .active {
          background: rgba(16, 185, 129, 0.2);
          color: #34d399;
          border: 1px solid #34d399;
        }

        .status-badge .inactive {
          background: rgba(239, 68, 68, 0.2);
          color: #fca5a5;
          border: 1px solid #ef4444;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .btn {
          padding: 0.9rem 1.8rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          transition: all 0.3s ease;
        }

        .btn.assign,
        .btn.confirm {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .btn.complete {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
        }

        .btn.cancel {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        }

        .btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
        }

        .officers-section .table-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          border: 1px solid rgba(16, 185, 129, 0.3);
          padding: 1.8rem;
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }

        .officers-section h3 {
          font-size: 1.6rem;
          color: #34d399;
          margin-bottom: 1.5rem;
        }

        .table-container {
          overflow-x: auto;
        }

        .officers-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .officers-table th {
          background: rgba(15, 23, 42, 0.9);
          padding: 1.2rem 1rem;
          text-align: left;
          color: #34d399;
          font-weight: 600;
          font-size: 0.95rem;
          border-bottom: 1px solid rgba(16, 185, 129, 0.3);
        }

        .officers-table td {
          padding: 1rem;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        }

        .officers-table .name {
          font-weight: 600;
          color: #ffffff;
        }

        .rank-badge {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 0.4rem 0.9rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-badge.small {
          padding: 0.4rem 0.8rem;
          font-size: 0.85rem;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .modal-card {
          background: rgba(30, 41, 59, 0.95);
          backdrop-filter: blur(16px);
          border-radius: 16px;
          border: 1px solid rgba(16, 185, 129, 0.4);
          width: 90%;
          max-width: 520px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
          animation: modalFade 0.3s ease-out;
        }

        @keyframes modalFade {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 1.8rem;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
          background: rgba(16, 185, 129, 0.15);
        }

        .modal-header h3 {
          margin: 0;
          color: #34d399;
          font-size: 1.5rem;
        }

        .close-btn {
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1.5rem;
          cursor: pointer;
        }

        .close-btn:hover {
          color: #ef4444;
        }

        .modal-body {
          padding: 1.8rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.6rem;
          color: #94a3b8;
          font-weight: 600;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 0.9rem 1rem;
          border-radius: 12px;
          border: 1px solid rgba(16, 185, 129, 0.4);
          background: rgba(15, 23, 42, 0.7);
          color: #e2e8f0;
          font-size: 1rem;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #34d399;
          box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
        }

        .add-rank {
          display: flex;
          gap: 1rem;
        }

        .add-btn {
          background: #10b981;
          color: white;
          border: none;
          width: 50px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.2rem;
        }

        .add-btn:hover {
          background: #059669;
        }

        .grade-list {
          margin-top: 1rem;
        }

        .grade-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 0.8rem;
          background: rgba(15, 23, 42, 0.5);
          border-radius: 10px;
        }

        .grade-item .rank {
          width: 140px;
          font-weight: 600;
          color: #34d399;
        }

        .grade-item input {
          width: 100px;
          padding: 0.7rem;
          border-radius: 8px;
          border: 1px solid rgba(16, 185, 129, 0.4);
          background: rgba(15, 23, 42, 0.7);
          color: #e2e8f0;
        }

        .remove-btn {
          background: #ef4444;
          color: white;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          cursor: pointer;
        }

        .remove-btn:hover {
          background: #dc2626;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
        }

        .modal-actions .btn {
          flex: 1;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .assign-wrapper { padding: 1rem; }
          .vip-card { flex-direction: column; text-align: center; gap: 1rem; }
          .action-buttons { justify-content: center; }
          .grade-item { flex-direction: column; align-items: stretch; }
          .grade-item .rank { width: auto; text-align: center; }
        }
      `}</style>
    </div>
  );
}




// import React, { useEffect, useState } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// /* ================= AXIOS ================= */
// const api = axios.create({ baseURL: BASE_URL });

// api.interceptors.request.use((config) => {
//   const role = localStorage.getItem("role");
//   const token = localStorage.getItem(`${role}Token`);
//   if (token) config.headers.Authorization = `Bearer ${token}`;
//   return config;
// });

// /* ================= DATE HELPERS ================= */
// const now = new Date();
// const plus8Hours = new Date(now.getTime() + 8 * 60 * 60 * 1000);

// const formatDateTimeLocal = (date) => {
//   const pad = (n) => n.toString().padStart(2, "0");
//   return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
//     date.getDate()
//   )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
// };

// export default function VipAutoAssign() {
//   const { state } = useLocation();
//   const navigate = useNavigate();
//   const vip = state?.vip;

//   /* ================= STATUS ================= */
//   const [vipStatus, setVipStatus] = useState("inactive");
//   const [result, setResult] = useState(null);
//   const [loading, setLoading] = useState(false);

//   /* ================= MODAL ================= */
//   const [showTimeModal, setShowTimeModal] = useState(false);

//   const getNowDateTimeLocal = () => {
//     const now = new Date();
//     const pad = (n) => n.toString().padStart(2, "0");

//     return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
//       now.getDate()
//     )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
//   };
//   const [startAt, setStartAt] = useState(getNowDateTimeLocal());
//   const [endAt, setEndAt] = useState(getNowDateTimeLocal());

//   /* ================= SECURITY GROUP ================= */
//   const [securityGroups, setSecurityGroups] = useState([]);
//   const [rankOptions, setRankOptions] = useState([]);
//   const [selectedGroupId, setSelectedGroupId] = useState("");
//   const [gradeList, setGradeList] = useState([]);
//   const [selectedGrade, setSelectedGrade] = useState("");

//   /* ================= GUARD ================= */
//   useEffect(() => {
//     if (!vip) {
//       navigate("/vip-management");
//       return;
//     }
//     fetchAssignedGuards();
//     // eslint-disable-next-line
//   }, [vip]);

//   /* ================= FETCH ASSIGNED ================= */
//   const fetchAssignedGuards = async () => {
//     try {
//       const res = await api.get(`/api/assignments/${vip.id}`);

//       // API RETURNS { data: { ... } }
//       const assignment = res.data?.data || res.data;

//       if (assignment && Array.isArray(assignment.officer)) {
//         setResult({
//           officers: assignment.officer,
//           status: assignment.status,
//           assignedAt: assignment.assignedAt,
//           startAt: assignment.startAt,
//           endAt: assignment.endAt,
//         });
//         setVipStatus("active");
//       } else {
//         setVipStatus("inactive");
//         setResult(null);
//       }
//     } catch (err) {
//       setVipStatus("inactive");
//       setResult(null);
//     }
//   };

//   /* ================= FETCH SECURITY ================= */
//   const fetchSecurityGroups = async () => {
//     try {
//       const role = localStorage.getItem("role");
//       const token = localStorage.getItem(`${role}Token`);

//       const res = await axios.get(`${BASE_URL}/api/setting/getsecuritygroup`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setSecurityGroups(res.data || []);
//     } catch {
//       toast.error("Failed to load security groups");
//     }
//   };

//   /* ================= FETCH RANKS ================= */
//   const fetchUniqueRanks = async () => {
//     try {
//       const role = localStorage.getItem("role");
//       const token = localStorage.getItem(`${role}Token`);

//       const res = await axios.get(`${BASE_URL}/api/officer/unique-ranks`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       setRankOptions(res.data || []);
//     } catch {
//       toast.error("Failed to load ranks");
//     }
//   };

//   /* ================= SELECT GROUP ================= */
//   const handleSelectGroup = (groupId) => {
//     setSelectedGroupId(groupId);
//     setSelectedGrade("");
//     setGradeList([]);

//     const group = securityGroups.find((g) => g.id === Number(groupId));
//     if (!group) return;

//     setGradeList(
//       group.values.map((v) => ({
//         rank: v.rank,
//         total: v.value,
//       }))
//     );
//   };

//   /* ================= ADD / EDIT RANK ================= */
//   const handleAddGrade = () => {
//     if (!selectedGrade) return toast.error("Select rank first");

//     if (gradeList.find((g) => g.rank === selectedGrade))
//       return toast.error("Rank already added");

//     setGradeList([...gradeList, { rank: selectedGrade, total: "" }]);
//     setSelectedGrade("");
//   };

//   const handleChange = (index, value) => {
//     const updated = [...gradeList];
//     updated[index].total = value;
//     setGradeList(updated);
//   };

//   const handleRemove = (index) => {
//     const updated = [...gradeList];
//     updated.splice(index, 1);
//     setGradeList(updated);
//   };

//   /* ================= AUTO ASSIGN ================= */
//   const handleAutoAssign = async () => {
//     if (!startAt || !endAt) return toast.error("Select start & end time");

//     if (gradeList.length === 0) return toast.error("Add at least one rank");

//     for (let g of gradeList) {
//       if (!g.total) {
//         toast.error(`Enter value for ${g.rank}`);
//         return;
//       }
//     }

//     const payload = {
//       userId: vip.id,
//       startAt,
//       endAt,
//       levels: gradeList.map((g) => ({
//         guardLevel: g.rank,
//         numberOfGuards: Number(g.total),
//       })),
//     };

//     try {
//       setLoading(true);
//       const res = await api.post(`/api/assignments/auto`, payload);
//       toast.success(res.data?.message || "Officers Assigned Successfully");
//       setShowTimeModal(false);
//       fetchAssignedGuards();
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Assignment Failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ================= COMPLETE / CANCEL ================= */
//   const handleMarkCompleted = async () => {
//     if (!window.confirm("Mark duty as COMPLETED?")) return;
//     try {
//       setLoading(true);
//       await api.post(`/api/assignments/complete/vip/${vip.id}/Completed`);
//       toast.success("Duty Completed");
//       setVipStatus("inactive");
//       setResult(null);
//     } catch {
//       toast.error("Failed to complete duty");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleMarkCanceled = async () => {
//     if (!window.confirm("Mark duty as CANCELED?")) return;
//     try {
//       setLoading(true);
//       await api.post(`/api/assignments/complete/vip/${vip.id}/Canceled`);
//       toast.success("Duty Canceled");
//       setVipStatus("inactive");
//       setResult(null);
//     } catch {
//       toast.error("Failed to cancel duty");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ================= UI ================= */
//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>VIP Officer Assignments</h2>

//       <div style={styles.card}>
//         <h3>{vip?.name}</h3>
//         <p>📧 {vip?.email}</p>
//         <p>🎖 {vip?.designation}</p>
//         <p>📞 {vip?.contactno}</p>

//         <span
//           className={`badge ${
//             vipStatus === "active" ? "bg-success" : "bg-danger"
//           }`}
//           style={{ padding: "8px 12px" }}
//         >
//           {vipStatus === "active" ? "Active" : "Inactive"}
//         </span>
//       </div>

//       {vipStatus === "active" && (
//         <>
//           <button style={styles.btn} onClick={handleMarkCompleted}>
//             Mark as Completed
//           </button>
//           <button style={styles.btncancel} onClick={handleMarkCanceled}>
//             Mark as Canceled
//           </button>
//         </>
//       )}

//       {vipStatus === "inactive" && (
//         <button
//           style={styles.btn}
//           onClick={() => {
//             fetchSecurityGroups();
//             fetchUniqueRanks();
//             setShowTimeModal(true);
//           }}
//         >
//           Auto Assign Officer
//         </button>
//       )}

//       {/* ================= MODAL ================= */}
//       {showTimeModal && (
//         <div style={styles.modalOverlay}>
//           <div style={styles.modalBox}>
//             <h3>Auto Assign Officers</h3>

//             <label>Start Time</label>
//             <input
//               type="datetime-local"
//               style={styles.input}
//               value={startAt}
//               min={getNowDateTimeLocal()} // 🔒 block past date/time
//               onChange={(e) => setStartAt(e.target.value)}
//             />

//             <label>End Time</label>
//             <input
//               type="datetime-local"
//               style={styles.input}
//               value={endAt}
//               min={startAt} // 🔒 end must be after start
//               onChange={(e) => setEndAt(e.target.value)}
//             />

//             <label>Security Group</label>
//             <select
//               style={styles.input}
//               value={selectedGroupId}
//               onChange={(e) => handleSelectGroup(e.target.value)}
//             >
//               <option value="">Select</option>
//               {securityGroups.map((g) => (
//                 <option key={g.id} value={g.id}>
//                   {g.name}
//                 </option>
//               ))}
//             </select>

//             {selectedGroupId && (
//               <>
//                 <label>Add Rank</label>
//                 <div style={{ display: "flex", gap: 10 }}>
//                   <select
//                     style={styles.input}
//                     value={selectedGrade}
//                     onChange={(e) => setSelectedGrade(e.target.value)}
//                   >
//                     <option value="">Select Rank</option>
//                     {rankOptions.map((r) => (
//                       <option key={r} value={r}>
//                         {r}
//                       </option>
//                     ))}
//                   </select>
//                   <button style={styles.btn} onClick={handleAddGrade}>
//                     +
//                   </button>
//                 </div>

//                 {gradeList.map((g, i) => (
//                   <div key={i} style={{ display: "flex", gap: 10 }}>
//                     <b style={{ width: 120 }}>{g.rank}</b>
//                     <input
//                       type="number"
//                       value={g.total}
//                       onChange={(e) => handleChange(i, e.target.value)}
//                     />
//                     <button onClick={() => handleRemove(i)}>❌</button>
//                   </div>
//                 ))}
//               </>
//             )}

//             <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
//               <button style={styles.btn} onClick={handleAutoAssign}>
//                 Continue
//               </button>
//               <button
//                 style={{ ...styles.btn, background: "#888" }}
//                 onClick={() => setShowTimeModal(false)}
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {result && (
//         <div style={styles.responseBox}>
//           <h3>Assigned Officers</h3>

//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.tableHeader}>Name</th>
//                 <th style={styles.tableHeader}>Email</th>
//                 <th style={styles.tableHeader}>Rank</th>
//                 <th style={styles.tableHeader}>Experience</th>
//                 <th style={styles.tableHeader}>Status</th>
//                 <th style={styles.tableHeader}>AssignedAt</th>
//                 <th style={styles.tableHeader}>StartAt</th>
//                 <th style={styles.tableHeader}>EndAt</th>
//               </tr>
//             </thead>

//             <tbody>
//               {result.officers.map((officer) => (
//                 <tr key={officer.id}>
//                   <td style={styles.tableCell}>{officer.name}</td>
//                   <td style={styles.tableCell}>{officer.email}</td>
//                   <td style={styles.tableCell}>{officer.rank}</td>
//                   <td style={styles.tableCell}>{officer.experience} yrs</td>
//                   <td style={styles.tableCell}>{result.status}</td>
//                   <td style={styles.tableCell}>
//                     {result.assignedAt
//                       ? new Date(
//                           result.assignedAt.split(".")[0]
//                         ).toLocaleString("en-IN", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: true,
//                         })
//                       : "—"}
//                   </td>

//                   <td style={styles.tableCell}>
//                     {result.startAt
//                       ? new Date(result.startAt).toLocaleString("en-IN", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: true,
//                         })
//                       : "—"}
//                   </td>
//                   <td style={styles.tableCell}>
//                     {result.endAt
//                       ? new Date(result.endAt).toLocaleString("en-IN", {
//                           year: "numeric",
//                           month: "2-digit",
//                           day: "2-digit",
//                           hour: "2-digit",
//                           minute: "2-digit",
//                           second: "2-digit",
//                           hour12: true,
//                         })
//                       : "—"}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}
//     </div>
//   );
// }

// /* ================= STYLES ================= */
// const styles = {
//   container: { padding: 30 },
//   title: {
//     fontSize: 28,
//     fontWeight: "bold",
//     marginBottom: 20,
//     color: "#1967d2",
//   },
//   card: {
//     background: "#fff",
//     padding: 20,
//     borderRadius: 12,
//     boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
//     marginBottom: 20,
//   },
//   btn: {
//     background: "#1967d2",
//     color: "#fff",
//     border: "none",
//     padding: "12px 20px",
//     borderRadius: 10,
//     cursor: "pointer",
//     fontSize: 16,
//     marginBottom: 20,
//   },
//   btncancel: {
//     background: "#f94225",
//     color: "#fff",
//     border: "none",
//     padding: "12px 20px",
//     borderRadius: 10,
//     cursor: "pointer",
//     fontSize: 16,
//     marginBottom: 20,
//     marginLeft: 5,
//   },
//   responseBox: {
//     background: "rgba(255,255,255,0.55)",
//     borderRadius: 14,
//     padding: 25,
//     boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
//   },
//   table: { width: "100%", marginTop: 15 },
//   tableHeader: { background: "#1967d2", color: "#fff", padding: 10 },
//   tableCell: { padding: 10, borderBottom: "1px solid #ddd" },
//   modalOverlay: {
//     position: "fixed",
//     inset: 0,
//     background: "rgba(0,0,0,0.6)",
//     display: "flex",
//     justifyContent: "center",
//     alignItems: "center",
//     zIndex: 999,
//   },
//   modalBox: { background: "#fff", padding: 25, borderRadius: 12, width: 450 },
//   input: {
//     width: "100%",
//     padding: "10px",
//     borderRadius: 8,
//     border: "1px solid #ccc",
//     marginTop: 5,
//     marginBottom: 15,
//   },
// };
